import { DatePipe } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  ClinicalTrialsService,
  Determinacion,
} from '@app/core/clinical-trials';
import { EntitiesService } from '@app/core/entities';
import { QueryService } from '@app/core/queries';
import { RuleSet } from '@app/shared/condition-builder';
import { TableLazyLoadEvent } from 'primeng/table';
import { allFields, Field } from './query-results.data';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import {
  buildPivotDynamicColumns,
  buildPivotViewRows,
  countPivotViewColumns,
  PivotMethodView,
} from './determinaciones-pivot.view';

const PIVOT_MAX_COLUMNS = 256;

@Component({
  selector: 'app-query-results',
  templateUrl: './query-results.component.html',
  styleUrls: ['./query-results.component.css'],
  providers: [DatePipe],
})
export class QueryResultsComponent implements OnInit {
  public id: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private entities: any;
  public queryName: string;
  public parsedData: Record<string, unknown>;
  public determinaciones: Determinacion[] = [];
  public totalCount = 0;
  public limit = 10;
  public offset = 0;
  public labType: string;
  private _historicDatabaseName?: string;
  public camposSensibilidad: string[] = [];
  public fields: Field[] = [];
  public debugQuery = '';
  public showDebugDialog = false;

  public viewMode: 'list' | 'pivot' = 'list';
  public viewModeOptions: { label: string; value: 'list' | 'pivot' }[] = [];
  public pivotMethods: PivotMethodView[] = [];
  public pivotLoading = false;
  public isPivotBlocked = false;
  public pivotBlockedMessage = '';
  public pivotDynamicColumns: { field: string; header: string }[] = [];
  public pivotRows: Record<string, unknown>[] = [];

  constructor(
    private queryService: QueryService,
    private route: ActivatedRoute,
    private entityService: EntitiesService,
    private clinicalTrialsService: ClinicalTrialsService,
    private cdr: ChangeDetectorRef,
    private translate: TranslateService,
    private toastr: ToastrService,
    private datePipe: DatePipe
  ) {}

  get showPivotToolbar(): boolean {
    return (
      this.labType === 'GEN' &&
      !!this.parsedData &&
      !this.parsedData['demographicDataOnly']
    );
  }

  changeRulesetFieldTitle(originalRuleset: RuleSet): RuleSet {
    const ruleset = JSON.parse(JSON.stringify(originalRuleset));

    ruleset.rules = ruleset.rules.map((rule: RuleSet) => {
      if (rule.condition) {
        return this.changeRulesetFieldTitle(rule);
      } else {
        const r = rule as RuleSet & { field?: string };
        r.field = this.entities[0].fields.find(
          (entity: { id: string }) => entity.id === r.field
        )?.title;
        return rule;
      }
    });
    return ruleset;
  }

  camelCaseToTitleCase(text: string): string {
    return text
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2')
      .replace(/^./, (str) => str.toUpperCase());
  }

  /**
   * Fecha-hora sin segundos (dd/MM/yyyy HH:mm). Misma lógica en vista lista y columnar.
   * Patrón numérico del DatePipe; no usa locale `es` para no exigir registerLocaleData en el bundle.
   */
  formatDeterminacionDateTime(value: unknown): string {
    const d = this.coercePivotDateTime(value);
    if (!d) {
      return value != null && value !== '' ? String(value) : '';
    }
    return this.datePipe.transform(d, 'dd/MM/yyyy HH:mm') ?? '';
  }

  /**
   * Vista lista: celdas con metadata `dateFilter` que incluye hora usan el mismo parseo Exasol que el pivot.
   */
  formatListFieldValue(field: Field, record: Record<string, unknown>): string {
    const raw = record[field.attribute];
    if (field.dateFilter?.includes('HH:mm')) {
      return this.formatDeterminacionDateTime(raw);
    }
    if (raw == null || raw === '') {
      return '';
    }
    return String(raw);
  }

  private coercePivotDateTime(value: unknown): Date | null {
    if (value == null || value === '') {
      return null;
    }
    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? null : value;
    }
    const s = String(value).trim();
    if (!s) {
      return null;
    }
    const normalized = s.includes('T') ? s : s.replace(' ', 'T');
    const d = new Date(normalized);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  ngOnInit(): void {
    this.viewModeOptions = [
      { label: this.translate.instant('QUERY.VIEW_MODE_LIST'), value: 'list' },
      { label: this.translate.instant('QUERY.VIEW_MODE_PIVOT'), value: 'pivot' },
    ];

    this.id = this.route.snapshot.params['id'];
    this.entityService.getEntities().subscribe((config) => {
      this.entities = config;
      if (this.id) {
        this.queryService.getQueryById(this.id).subscribe((query) => {
          this.queryName = query.title;
          this.parsedData = JSON.parse(query.data) as Record<string, unknown>;
          this._historicDatabaseName = query.historicDatabaseName;
          this.labType = query.labType;
          this.fields =
            (this.parsedData['fields'] as string[])?.length > 0
              ? allFields.filter((field) =>
                  (this.parsedData['fields'] as string[])
                    .map((f) => f.toUpperCase())
                    .includes(field.id)
                )
              : allFields;
          this.refreshDeterminaciones(this.limit, this.offset, false);
        });
      }
    });
  }

  public onViewModeChange(): void {
    if (this.viewMode === 'list') {
      this.isPivotBlocked = false;
      this.pivotBlockedMessage = '';
      this.pivotMethods = [];
      this.pivotDynamicColumns = [];
      this.pivotRows = [];
      this.offset = 0;
      this.refreshDeterminaciones(this.limit, 0, false);
      return;
    }
    if (!this.showPivotToolbar) {
      return;
    }
    void this.enterPivotView();
  }

  private async enterPivotView(): Promise<void> {
    this.pivotLoading = true;
    this.isPivotBlocked = false;
    this.pivotBlockedMessage = '';
    this.pivotRows = [];
    this.cdr.detectChanges();

    this.clinicalTrialsService
      .findPivotMethods(this.labType, this.parsedData as never, this._historicDatabaseName)
      .subscribe({
        next: (res) => {
          this.pivotMethods = (res.methods || []) as PivotMethodView[];
          const n = countPivotViewColumns(this.pivotMethods);
          if (n > PIVOT_MAX_COLUMNS) {
            this.isPivotBlocked = true;
            this.pivotBlockedMessage = this.translate.instant('QUERY.PIVOT_LIMIT_BLOCKED', {
              count: n,
            });
            this.pivotLoading = false;
            this.cdr.detectChanges();
            return;
          }
          this.isPivotBlocked = false;
          const suffix = this.translate.instant('QUERY.PIVOT_SECONDARY_SUFFIX');
          this.pivotDynamicColumns = buildPivotDynamicColumns(this.pivotMethods, suffix);
          this.offset = 0;
          this.refreshDeterminaciones(this.limit, 0, true);
        },
        error: (err) => {
          this.pivotLoading = false;
          this.debugQuery = err?.error?.debug?.query || err?.debug?.query || '';
          const message =
            err?.error?.message || err?.message || 'Error al cargar columnas pivot';
          alert(message);
          this.viewMode = 'list';
          this.cdr.detectChanges();
        },
      });
  }

  loadDataLazy(event: TableLazyLoadEvent): void {
    if (event.rows != this.limit || event.first != this.offset) {
      this.limit = event.rows ?? 10;
      this.offset = event.first ?? 0;
      const pivot =
        this.viewMode === 'pivot' && this.showPivotToolbar && !this.isPivotBlocked;
      this.refreshDeterminaciones(this.limit, this.offset, pivot);
    }
  }

  private refreshDeterminaciones(limit: number, offset: number, pivotByCodPeticion: boolean): void {
    this.clinicalTrialsService
      .findDeterminaciones(
        this.labType,
        this.parsedData as never,
        this._historicDatabaseName,
        limit,
        offset,
        pivotByCodPeticion
      )
      .subscribe({
        next: (data) => {
          this.determinaciones = data.data;
          this.totalCount = data.count;
          this.debugQuery = data.debug?.query || '';
          if (this.determinaciones.length === 0) {
            if (this.viewMode !== 'pivot') {
              alert('No se encontraron resultados');
            }
            this.camposSensibilidad = [];
          } else {
            this.camposSensibilidad = Object.keys(this.determinaciones[0]).filter(
              (key) =>
                key.startsWith('sensibilidad') ||
                (key.startsWith('cmi') && key !== 'cmi') ||
                key.startsWith('descAntibiotico')
            );
          }
          if (this.viewMode === 'pivot' && !this.isPivotBlocked) {
            this.pivotRows = buildPivotViewRows(
              this.determinaciones as unknown as Record<string, unknown>[],
              this.pivotMethods
            );
          }
          this.pivotLoading = false;
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.determinaciones = [];
          this.totalCount = 0;
          this.pivotRows = [];
          this.debugQuery = err?.error?.debug?.query || err?.debug?.query || '';
          const message =
            err?.error?.message || err?.message || 'Error al ejecutar la consulta';
          alert(message);
          this.pivotLoading = false;
          this.cdr.detectChanges();
        },
      });
  }

  public exportPivotCsv(): void {
    if (this.isPivotBlocked || this.viewMode !== 'pivot') {
      return;
    }
    this.clinicalTrialsService
      .createPivotExport(
        this.parsedData as never,
        this.queryName,
        this._historicDatabaseName
      )
      .subscribe({
        next: () => {
          this.toastr.success(this.translate.instant('QUERY.PIVOT_EXPORT_QUEUED'));
        },
        error: (err) => {
          const msg =
            err?.error?.message ||
            err?.message ||
            this.translate.instant('QUERY.PIVOT_EXPORT_ERROR');
          this.toastr.error(msg);
        },
      });
  }

  back(): void {
    window.history.back();
  }

  openDebugDialog(): void {
    this.showDebugDialog = true;
  }

  closeDebugDialog(): void {
    this.showDebugDialog = false;
  }

  copyToClipboard(): void {
    if (this.debugQuery) {
      navigator.clipboard.writeText(this.debugQuery).then(
        () => console.log('Query copiada al portapapeles'),
        (err) => console.error('Error al copiar al portapapeles:', err)
      );
    }
  }
}
